<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import { canonicalTimeZone, timeZoneEntries, timeZoneLabel } from '@/components/Sites/siteTemplates'

// Searchable IANA time-zone picker (Trello 1GamnIFQ): a combobox button that
// opens a popover with a filter input and a listbox capped at 20rem, grouped
// by region and labelled with the zone's current UTC offset. A native
// <select> with ~420 options opened as a full-screen list on scroll.
const props = defineProps({
	modelValue: {
		type: String,
		default: '',
	},
	id: {
		type: String,
		default: 'time-zone',
	},
	label: {
		type: String,
		default: 'Time zone',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
})
const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const query = ref('')
const active = ref(-1) // index into `flat`
const root = ref(null)
const trigger = ref(null)
const search = ref(null)
const list = ref(null)
const popover = ref(null)

// A zone saved under its old name (`Asia/Saigon`) selects the option the list shows (`Asia/Ho_Chi_Minh`).
const selected = computed(() => canonicalTimeZone(props.modelValue))
const entries = computed(() => timeZoneEntries(selected.value))
const selectedLabel = computed(() => timeZoneLabel(selected.value || 'UTC', entries.value))

/** Case- and punctuation-insensitive match on the zone name, its city and the offset. */
const norm = (s) => String(s).toLowerCase().replace(/[_/\s]+/g, ' ').trim()
// One or two letters match the start of a word only ("t" → Tokyo, Toronto…, not every
// "gmt" label); longer terms and offsets ("+5:30", "-4") match anywhere.
const matches = (search, term) => {
	if (term.length >= 3 || /^[+\-\d]/.test(term)) return search.includes(term)
	return search.startsWith(term) || search.includes(` ${term}`)
}
const filtered = computed(() => {
	const q = norm(query.value)
	if (!q) return entries.value
	const terms = q.split(' ').filter(Boolean)
	return entries.value.filter((z) => terms.every((t) => matches(z.search, t)))
})
/** Options in display order, grouped by region; `index` points into `flat`. */
const groups = computed(() => {
	const out = []
	filtered.value.forEach((z, index) => {
		if (out[out.length - 1]?.region !== z.region) out.push({ region: z.region, zones: [] })
		out[out.length - 1].zones.push({ ...z, index })
	})
	return out
})
const groupId = (region) => `${props.id}-group-${String(region).replace(/[^\w-]+/g, '-')}`
const flat = computed(() => filtered.value)

const optionId = (i) => `${props.id}-option-${i}`
const activeId = computed(() => (active.value >= 0 && flat.value[active.value] ? optionId(active.value) : undefined))

const scrollActiveIntoView = () => {
	if (!list.value || !activeId.value) return
	list.value.querySelector(`#${CSS.escape(activeId.value)}`)?.scrollIntoView({ block: 'nearest' })
}
const setActive = (i) => {
	if (!flat.value.length) {
		active.value = -1
		return
	}
	active.value = Math.max(0, Math.min(flat.value.length - 1, i))
	nextTick(scrollActiveIntoView)
}

const show = async (seed = '') => {
	if (props.disabled || open.value) return
	open.value = true
	query.value = seed
	await nextTick()
	const i = seed ? 0 : flat.value.findIndex((z) => z.value === selected.value)
	active.value = flat.value.length ? i : -1
	search.value?.focus()
	nextTick(() => {
		scrollActiveIntoView()
		// The list is capped at 20rem; when the field sits near the bottom of the
		// page, bring the whole popover on screen rather than leaving it clipped.
		popover.value?.scrollIntoView({ block: 'nearest' })
	})
}
const hide = ({ focusTrigger = true } = {}) => {
	if (!open.value) return
	open.value = false
	query.value = ''
	active.value = -1
	if (focusTrigger) trigger.value?.focus()
}
const toggle = () => (open.value ? hide() : show())

const choose = (zone) => {
	if (!zone) return
	emit('update:modelValue', zone.value)
	hide()
}

const onTriggerKeydown = (e) => {
	if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
		e.preventDefault()
		show()
	} else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
		// Type-ahead like a native select: the first letter opens the list already filtered.
		e.preventDefault()
		show(e.key)
	}
}
const onSearchKeydown = (e) => {
	switch (e.key) {
		case 'ArrowDown':
			e.preventDefault()
			setActive(active.value + 1)
			break
		case 'ArrowUp':
			e.preventDefault()
			setActive(active.value - 1)
			break
		case 'Home':
			e.preventDefault()
			setActive(0)
			break
		case 'End':
			e.preventDefault()
			setActive(flat.value.length - 1)
			break
		case 'PageDown':
			e.preventDefault()
			setActive(active.value + 10)
			break
		case 'PageUp':
			e.preventDefault()
			setActive(active.value - 10)
			break
		case 'Enter':
			e.preventDefault()
			choose(flat.value[active.value] || (flat.value.length === 1 ? flat.value[0] : null))
			break
		case 'Escape':
			e.preventDefault()
			hide()
			break
		case 'Tab':
			hide({ focusTrigger: false })
			break
		default:
	}
}
// Typing narrows the list; keep the highlight on the first match.
watch(query, () => {
	if (!open.value) return
	const i = flat.value.findIndex((z) => z.value === selected.value)
	active.value = i >= 0 && !query.value ? i : flat.value.length ? 0 : -1
	nextTick(scrollActiveIntoView)
})

const onDocumentPointer = (e) => {
	if (open.value && root.value && !root.value.contains(e.target)) hide({ focusTrigger: false })
}
watch(open, (v) => {
	if (v) document.addEventListener('pointerdown', onDocumentPointer, true)
	else document.removeEventListener('pointerdown', onDocumentPointer, true)
})
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocumentPointer, true))

const listboxId = computed(() => `${props.id}-listbox`)
</script>

<template>
	<div ref="root" class="relative w-full" :data-testid="`${id}-field`">
		<label v-if="label" :id="`${id}-label`" class="mb-2 block font-medium text-tiny text-surface-text-1" :for="id" @click.prevent="trigger?.focus()">{{ label }}</label>
		<button
			:id="id"
			ref="trigger"
			type="button"
			role="combobox"
			aria-haspopup="listbox"
			:aria-expanded="open"
			:aria-controls="open ? listboxId : undefined"
			:aria-labelledby="label ? `${id}-label ${id}` : undefined"
			:disabled="disabled"
			:data-value="selected"
			class="flex h-8 w-full items-center gap-2 rounded border-neutral-n50 bg-surface-3 pl-3 pr-2.5 text-left transition-colors hover:bg-surface-4 focus:bg-surface-4 focus:outline-0 focus:ring-0 disabled:cursor-not-allowed disabled:text-surface-7 text-base md:text-tiny"
			:class="open ? 'bg-surface-4 text-white' : 'text-surface-text-2'"
			@click="toggle"
			@keydown="onTriggerKeydown"
		>
			<span class="min-w-0 flex-1 truncate" :data-testid="`${id}-value`">{{ selectedLabel }}</span>
			<icon-base class="shrink-0 text-surface-7 transition-transform" :class="open && 'rotate-180'" :width="16" :height="16" :width-view-box="16" :height-view-box="16" fill="none">
				<icon-chevron-down />
			</icon-base>
		</button>

		<div
			v-if="open"
			ref="popover"
			class="absolute inset-x-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-surface-4 bg-surface-2 shadow-xl"
			:data-testid="`${id}-popover`"
		>
			<div class="relative border-b border-surface-4 p-2">
				<icon-base class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-surface-7" :width="16" :height="16"><icon-search /></icon-base>
				<input
					ref="search"
					v-model="query"
					type="text"
					role="searchbox"
					autocomplete="off"
					autocapitalize="off"
					spellcheck="false"
					placeholder="Search city or offset, e.g. Chicago or GMT-5"
					aria-label="Search time zones"
					:aria-controls="listboxId"
					:aria-activedescendant="activeId"
					class="h-8 w-full rounded bg-surface-3 pl-8 pr-3 text-surface-text-2 placeholder:text-surface-6 focus:bg-surface-4 focus:outline-0 focus:ring-0 text-base md:text-tiny"
					:data-testid="`${id}-search`"
					@keydown="onSearchKeydown"
				>
			</div>
			<!-- No top padding: the sticky region headers must sit flush with the scroll edge, or rows show through the gap above them. -->
			<ul :id="listboxId" ref="list" role="listbox" :aria-labelledby="label ? `${id}-label` : undefined" class="m-0 max-h-80 list-none overflow-y-auto overscroll-contain p-0 pb-1" :data-testid="`${id}-listbox`">
				<li v-for="group in groups" :key="group.region" role="presentation">
					<!-- h-7 here and scroll-mt-7 on the options must match, so keyboard navigation stops rows below the header. -->
					<div
						:id="groupId(group.region)"
						role="presentation"
						class="sticky top-0 z-10 flex h-7 items-center border-b border-surface-3 bg-surface-2 px-3.5 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6"
						:data-testid="`${id}-group-header`"
					>
						{{ group.region }}
					</div>
					<ul role="group" :aria-labelledby="groupId(group.region)" class="m-0 list-none px-1 py-0.5">
						<li
							v-for="row in group.zones"
							:id="optionId(row.index)"
							:key="row.value"
							role="option"
							:aria-selected="row.value === selected"
							class="flex h-8 scroll-mt-7 cursor-pointer items-center gap-2 rounded px-2.5 text-tiny"
							:class="[
								row.index === active ? 'bg-surface-4 text-white' : 'text-surface-text-1 hover:bg-surface-3 hover:text-white',
							]"
							:data-value="row.value"
							@pointerenter="active = row.index"
							@click="choose(row)"
						>
							<span class="min-w-0 flex-1 truncate">{{ row.text }}</span>
							<span class="shrink-0 tabular-nums text-surface-7">{{ row.offsetLabel }}</span>
							<icon-base v-if="row.value === selected" class="shrink-0 text-white" :width="14" :height="14" :width-view-box="16" :height-view-box="16" fill="none"><icon-checkmark /></icon-base>
						</li>
					</ul>
				</li>
				<li v-if="!flat.length" class="px-2.5 py-3 text-center text-tiny text-surface-7" role="presentation" :data-testid="`${id}-empty`">No time zone matches “{{ query }}”.</li>
			</ul>
		</div>
	</div>
</template>
